import { DateFilterTypeEnum } from 'src/app/enums/dateFilterType.enum';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { parseSortColumn } from '../_shared/widget-table-sort';

export const SegProfileSortColumn = {
    SegName: 'segName',
    AgentName: 'agentName',
    TypeLabel: 'typeLabel',
    AssessedBandName: 'assessedBandName',
    CurrentBandName: 'currentBandName',
    DecisionStatisticPercentOfOel: 'decisionStatisticPercentOfOel',
    LiveSampleCount: 'liveSampleCount',
    PlannedNumberOfSamples: 'plannedNumberOfSamples',
    CoverageRatio: 'coverageRatio',
    GeoStdDev: 'geoStdDev',
    DistributionLabel: 'distributionLabel',
    DecisionStatisticValue: 'decisionStatisticValue',
    NumberOfWorkers: 'numberOfWorkers',
    NumberOfContractors: 'numberOfContractors',
} as const;

export type SegProfileSortColumn = typeof SegProfileSortColumn[keyof typeof SegProfileSortColumn];

export const SEG_PROFILE_SORT_COLUMNS: readonly SegProfileSortColumn[] = Object.values(SegProfileSortColumn);

export const DEFAULT_SORT_COLUMN: SegProfileSortColumn = SegProfileSortColumn.DecisionStatisticPercentOfOel;
export const DEFAULT_SORT_ASC = false;

export interface SegProfileFilterValues {
    agentId: string | null;
    surveyTypeIds: string[];
    dateFilterType: number | undefined;
    customStartDate: string | null;
    customEndDate: string | null;
    sortColumn: SegProfileSortColumn;
    sortAsc: boolean;
}

export const SEG_PROFILE_FILTER_DEFAULTS: SegProfileFilterValues = {
    agentId: null,
    surveyTypeIds: [],
    dateFilterType: DateFilterTypeEnum.ThisYear,
    customStartDate: null,
    customEndDate: null,
    sortColumn: DEFAULT_SORT_COLUMN,
    sortAsc: DEFAULT_SORT_ASC,
};

const agentIdKey = nameOf<SegProfileFilterValues>((x) => x.agentId);
const surveyTypeIdsKey = nameOf<SegProfileFilterValues>((x) => x.surveyTypeIds);
const dateFilterKey = nameOf<SegProfileFilterValues>((x) => x.dateFilterType);
const customStartKey = nameOf<SegProfileFilterValues>((x) => x.customStartDate);
const customEndKey = nameOf<SegProfileFilterValues>((x) => x.customEndDate);
const sortColumnKey = nameOf<SegProfileFilterValues>((x) => x.sortColumn);
const sortAscKey = nameOf<SegProfileFilterValues>((x) => x.sortAsc);

export function configToFilterValues(
    config: Record<string, unknown>,
): SegProfileFilterValues {
    return {
        agentId: config[agentIdKey] != null ? (config[agentIdKey] as string) : null,
        surveyTypeIds: Array.isArray(config[surveyTypeIdsKey])
            ? (config[surveyTypeIdsKey] as unknown[]).filter((id): id is string => typeof id === 'string')
            : [],
        dateFilterType: config[dateFilterKey] as number | undefined,
        customStartDate: typeof config[customStartKey] === 'string' ? (config[customStartKey] as string) : null,
        customEndDate: typeof config[customEndKey] === 'string' ? (config[customEndKey] as string) : null,
        sortColumn: parseSortColumn(config[sortColumnKey], SEG_PROFILE_SORT_COLUMNS, DEFAULT_SORT_COLUMN),
        sortAsc: typeof config[sortAscKey] === 'boolean' ? (config[sortAscKey] as boolean) : DEFAULT_SORT_ASC,
    };
}

export function filterValuesToConfig(
    values: SegProfileFilterValues,
): Record<string, unknown> {
    const config: Record<string, unknown> = {};
    config[agentIdKey] = values.agentId;
    config[surveyTypeIdsKey] = values.surveyTypeIds;
    if (values.dateFilterType != null) config[dateFilterKey] = values.dateFilterType;
    if (values.customStartDate) config[customStartKey] = values.customStartDate;
    if (values.customEndDate) config[customEndKey] = values.customEndDate;
    if (values.sortColumn) config[sortColumnKey] = values.sortColumn;
    config[sortAscKey] = values.sortAsc;
    return config;
}

export const INCLUDED_DATE_FILTERS = new Set([
    DateFilterTypeEnum.AllDates,
    DateFilterTypeEnum.ThisYear,
    DateFilterTypeEnum.ThisQuarter,
    DateFilterTypeEnum.ThisMonth,
    DateFilterTypeEnum.LastYear,
    DateFilterTypeEnum.LastQuarter,
    DateFilterTypeEnum.LastMonth,
    DateFilterTypeEnum.Custom,
]);

export const DATE_LABEL_OVERRIDES: Record<number, string> = {
    [DateFilterTypeEnum.AllDates]: 'All Dates',
    [DateFilterTypeEnum.ThisMonth]: 'This Month',
    [DateFilterTypeEnum.ThisQuarter]: 'This Quarter',
    [DateFilterTypeEnum.ThisYear]: 'Year to Date',
    [DateFilterTypeEnum.LastMonth]: 'Last Month',
    [DateFilterTypeEnum.LastQuarter]: 'Last Quarter',
    [DateFilterTypeEnum.LastYear]: 'Last 12 Months',
    [DateFilterTypeEnum.Custom]: 'Custom',
};

const DATE_FILTER_ORDER: number[] = [
    DateFilterTypeEnum.AllDates,
    DateFilterTypeEnum.ThisMonth,
    DateFilterTypeEnum.ThisQuarter,
    DateFilterTypeEnum.ThisYear,
    DateFilterTypeEnum.LastMonth,
    DateFilterTypeEnum.LastQuarter,
    DateFilterTypeEnum.LastYear,
    DateFilterTypeEnum.Custom,
];

export const DATE_RANGE_OPTIONS = DATE_FILTER_ORDER
    .filter((id) => INCLUDED_DATE_FILTERS.has(id))
    .map((id) => ({
        value: id,
        label: DATE_LABEL_OVERRIDES[id] || DateFilterTypeEnum[id],
    }));

export interface SegProfileTableHeader {
    key: SegProfileSortColumn;
    label: string;
}

export const SEG_PROFILE_TABLE_HEADERS: SegProfileTableHeader[] = [
    { key: SegProfileSortColumn.SegName, label: 'SEG' },
    { key: SegProfileSortColumn.AgentName, label: 'Agent' },
    { key: SegProfileSortColumn.TypeLabel, label: 'Type' },
    { key: SegProfileSortColumn.AssessedBandName, label: 'Assessed' },
    { key: SegProfileSortColumn.CurrentBandName, label: 'Current' },
    { key: SegProfileSortColumn.DecisionStatisticPercentOfOel, label: 'Decision Statistic' },
    { key: SegProfileSortColumn.LiveSampleCount, label: 'No. of Samples' },
    { key: SegProfileSortColumn.PlannedNumberOfSamples, label: 'Planned (Annual)' },
    { key: SegProfileSortColumn.CoverageRatio, label: 'Coverage' },
    { key: SegProfileSortColumn.GeoStdDev, label: 'GSD' },
    { key: SegProfileSortColumn.DistributionLabel, label: 'Distribution' },
    { key: SegProfileSortColumn.DecisionStatisticValue, label: 'Decision Statistic Value' },
    { key: SegProfileSortColumn.NumberOfWorkers, label: 'Employees' },
    { key: SegProfileSortColumn.NumberOfContractors, label: 'Contractors' },
];
